/**
 * Gift-domain error codes (octofort_api `APIErrorCode`, octofort_api/api.py).
 *
 * A leaf module ON PURPOSE: `client.ts` needs these to decide which coded errors
 * the claim page renders itself, and `ApiGift.ts` imports `client.ts` — so these
 * cannot live in ApiGift without an import cycle.
 *
 * The claim endpoint surfaces every one of these as HTTP 400 with a BR-PT
 * `{ message, code }` body.
 */

export const GIFT_DISABLED = 9000;
export const GIFT_ALREADY_CLAIMED = 9001;
export const GIFT_EXPIRED = 9002;
export const GIFT_SELF_CLAIM = 9003;
export const GIFT_NOT_RECIPIENT = 9004;
export const GIFT_NOT_FOUND = 9008;
export const GIFT_RETRACTED = 9009;
export const GIFT_REJECTED = 9010;
/**
 * Sender-side terminal state, raised by RETRACT/REJECT (never by claim) when the
 * gift is no longer actionable (already expired/refunded/rejected). Deliberately
 * NOT in GIFT_CLAIM_CODES — the claim page never sees it; the sender's outbox
 * surfaces the backend's BR-PT message via the global interceptor.
 */
export const GIFT_NOT_AVAILABLE = 9011;
/**
 * INV-1 server backstop, raised at CHECKOUT (POST /api/order/) when a gift_links
 * line has quota≠1. It is a SEND-side code, never a claim-side one, so it is
 * deliberately NOT in GIFT_CLAIM_CODES — the cart renders its own human screen
 * (gift.linkQuotaBlocked) and the interceptor suppresses the generic toast for it
 * on the order endpoint only. See utils/giftIntent / Cart handlePayment.
 */
export const GIFT_LINK_QUOTA_INVALID = 9012;

// Register-to-claim validation codes, raised by RegisterSerializer on the claim POST.
export const PHONE_NUMBER_EXISTS = 1001;
export const VERIFICATION_CODE_FREEZE = 1003;
export const VERIFICATION_CODE_INVALID = 1004;
export const SIGNUP_UNDER_AGE = 1007;
export const PHONE_NUMBER_INVALID = 1010;

/**
 * Codes the /presente/<token> page renders as its own screen or field error.
 * Suppresses the global danger snackbar for these — and ONLY on the claim URL,
 * so the gift inbox/outbox and the normal registration flow keep their toasts.
 *
 * GIFT_NOT_AVAILABLE (9011) is deliberately absent: it is reachable only via
 * retract/reject, never via claim — the sender's outbox handles it, not this page.
 *
 * 1001/1003/1004 are already globally suppressed by the interceptor; they are
 * listed here for completeness of the claim contract, not because this list is
 * what suppresses them.
 */
export const GIFT_CLAIM_CODES: number[] = [
  GIFT_DISABLED,
  GIFT_ALREADY_CLAIMED,
  GIFT_EXPIRED,
  GIFT_SELF_CLAIM,
  GIFT_NOT_RECIPIENT,
  GIFT_NOT_FOUND,
  GIFT_RETRACTED,
  GIFT_REJECTED,
  SIGNUP_UNDER_AGE,
  PHONE_NUMBER_INVALID,
];

/** True for the public share-by-link claim endpoint (GET preview / POST claim). */
export const isGiftClaimUrl = (url: string): boolean =>
  url.includes("/api/gift/claim/");
